import '@diekeure/dcr-components/dcr-dialog.js';
import '@diekeure/dcr-components/dcr-text-field.js';
import '@diekeure/dcr-components/dcr-select.js';
import '@diekeure/dcr-components/dcr-select-option.js';
import '@diekeure/dcr-components/dcr-checkbox.js';

export type MountTarget = Element | string;

export interface InputField {
  field: HTMLElement;
  value: () => string;
  setValue: (value: string | number) => void;
  checked: () => boolean;
  setChecked: (checked: boolean) => void;
}

export interface SelectField {
  field: HTMLElement;
  value: () => string;
  setValue: (value: string) => void;
}

export function readConfig<T>(el: HTMLElement, defaults: T): T {
  try {
    return JSON.parse(el.getAttribute('data-cb-config') || '{}') as T;
  } catch {
    return defaults;
  }
}

export function newDialog(
  title: string,
  onCancel: () => void,
  mountTarget: MountTarget = document.body
): HTMLElement {
  const dialog = document.createElement('dcr-dialog');
  dialog.setAttribute('headline', title);
  dialog.setAttribute('type', 'confirm-cancel');
  dialog.setAttribute('confirm-label', 'Voeg in');
  dialog.setAttribute('cancel-label', 'Annuleren');
  dialog.setAttribute('open', '');

  const content = document.createElement('div');
  content.setAttribute('id', 'cds-dialog-content');
  content.classList.add('flex', 'flex-column', 'gap-m');
  dialog.append(content);

  dialog.addEventListener('confirm', () => {
    dialog.dispatchEvent(new CustomEvent('onSubmit', { bubbles: true, cancelable: true }));
  });
  dialog.addEventListener('cancel', () => {
    onCancel();
  });
  dialog.addEventListener('closed', () => {
    dialog.remove();
  });

  const mount = typeof mountTarget === 'string' ? document.querySelector(mountTarget) : mountTarget;
  (mount ?? document.body).append(dialog);

  return dialog;
}

export function createSelect(
  id: string,
  label: string,
  options: { label: string; value: any }[],
  size?: number
): SelectField {
  const select = document.createElement('dcr-select');
  select.setAttribute('id', id);
  if (label) select.setAttribute('label', label);
  if (size) (select as HTMLElement).style.width = `${size}px`;

  options.forEach((option) => {
    const optionEl = document.createElement('dcr-select-option');
    optionEl.setAttribute('value', String(option.value));
    optionEl.setAttribute('label', option.label);
    optionEl.textContent = option.label;
    select.append(optionEl);
  });

  return {
    field: select,
    value: () => (select as any).value ?? '',
    setValue: (v) => {
      (select as any).value = String(v);
    },
  };
}

export function createInput(
  id: string,
  label: string,
  type: string,
  supportingText?: string,
  size?: number
): InputField {
  if (type === 'checkbox' || type === 'radio') {
    const checkbox = document.createElement('dcr-checkbox');
    checkbox.setAttribute('id', id);
    checkbox.setAttribute('name', id);
    if (label) checkbox.setAttribute('label', label);
    if (supportingText) checkbox.setAttribute('supporting-text', supportingText);

    return {
      field: checkbox,
      value: () => (checkbox as any).value ?? '',
      setValue: (v) => {
        (checkbox as any).value = String(v);
      },
      checked: () => !!(checkbox as any).checked,
      setChecked: (c) => {
        (checkbox as any).checked = !!c;
      },
    };
  }

  const textField = document.createElement('dcr-text-field');
  textField.setAttribute('id', id);
  textField.setAttribute('name', id);
  textField.setAttribute('type', type);
  if (label) textField.setAttribute('label', label);
  if (supportingText) textField.setAttribute('supporting-text', supportingText);
  if (size) (textField as HTMLElement).style.width = `${size}px`;

  return {
    field: textField,
    value: () => (textField as any).value ?? '',
    setValue: (v) => {
      (textField as any).value = String(v);
    },
    checked: () => !!(textField as any).checked,
    setChecked: (c) => {
      (textField as any).checked = !!c;
    },
  };
}

export function createButton(label: string) {
  const btn = document.createElement('button');
  btn.innerHTML = label;
  btn.classList.add(
    'inline-flex',
    'primary-container',
    'w-fit',
    'border',
    'corner-full',
    'outline-none',
    'cursor-pointer',
    'elevation-0',
    'hover:elevation-1',
    'transition-elevation',
    'p-i-xs',
    'p-b-2xs',
    'label-large'
  );

  return btn;
}

export function styleStroke(el) {
  el.setAttribute('fill', 'none');
  el.setAttribute('stroke-width', '2');
  el.setAttribute('stroke-linecap', 'round');
  el.setAttribute('stroke-linejoin', 'round');
  el.setAttribute('vector-effect', 'non-scaling-stroke');
  el.classList.add('stroke-stroke');
}

export function createTabs(tabs, dynamic = false) {
  const activeClassList = ['border-be-primary', 'primary-container'];
  const tabWrapper = document.createElement('div');
  tabWrapper.classList.add('tabs-wrapper', 'flex', 'flex-column', 'gap-m');

  const tabHeaders = document.createElement('div');
  tabHeaders.classList.add('tab-headers', 'flex', 'gap-s');

  const tabContents = document.createElement('div');
  tabContents.classList.add('tab-contents', 'flex', 'flex-column', 'gap-m');

  const handleTabClick = (index) => {
    const tabs = tabHeaders.querySelectorAll('.tab-header');
    const contents = tabContents.querySelectorAll('.tab-content');

    tabs.forEach((tab, i) => {
      if (i === index) {
        tab.classList.add(...activeClassList);
        contents[i].classList.remove('hidden');
      } else {
        tab.classList.remove(...activeClassList);
        contents[i]?.classList.add('hidden');
      }
    });
  };

  const addTab = (tab, insertBeforeOffset = 1) => {
    const index = tabHeaders.children.length - insertBeforeOffset;

    const tabHeader = document.createElement('button');
    tabHeader.classList.add('tab-header', 'p-xs', 'cursor-pointer', 'label-medium', 'border-b-2', 'border-transparent');
    tabHeader.setAttribute('data-index', index.toString());
    tabHeader.innerHTML = tab.title;

    const tabContent = document.createElement('div');
    tabContent.classList.add('tab-content', 'p-m', 'bg-white', 'corner-s', 'border', 'border-stroke-variant');
    tabContent.append(tab.content.field ?? tab.content.block ?? tab.content);

    if (index !== 0) tabContent.classList.add('hidden');
    else tabHeader.classList.add(...activeClassList);

    tabHeader.addEventListener('click', (event) =>
      handleTabClick(+(event.target as HTMLDivElement).getAttribute('data-index'))
    );

    if (insertBeforeOffset) {
      tabHeaders.insertBefore(tabHeader, tabHeaders.children[tabHeaders.children.length - 2]);
      tabContents.insertBefore(tabContent, tabContents.children[tabContents.children.length - 1]);
    } else {
      tabHeaders.appendChild(tabHeader);
      tabContents.appendChild(tabContent);
    }

    Array.from(tabHeaders.children).forEach((tab, i) => {
      tab.setAttribute('data-index', i.toString());
    });
  };

  tabs.forEach((tab) => addTab(tab, 0));

  let addButton;
  if (dynamic) {
    addButton = document.createElement('button');
    addButton.classList.add('tab-header', 'p-xs', 'cursor-pointer', 'label-medium', 'border', 'border', 'corner-full');
    addButton.innerHTML = '+ Toevoegen';

    tabHeaders.appendChild(addButton);
  }

  tabWrapper.append(tabHeaders, tabContents);

  return { tabWrapper, addButton, addTab };
}
